import React, { lazy, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { initCoaTableScrollShadow } from "./pages/Report/utils/coaTableScrollShadow";
import NetworkOfflineDialog from "./components/NetworkStatus/NetworkOfflineDialog";
import Forgotpassword from "./pages/common/forgotpassword/forgotpasswordform";
import Login from "./pages/common/login/login";
import Logout from "./pages/common/logout/Logout";
import OTPverification from "./pages/common/otpverification/otpverificationform";
import Resetpassword from "./pages/common/resetpassword/resetpassword";
import ProtectedRoutes from "./protectedroute";
import PermissionRoute from "./PermissionRoute";
import { Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import useInactivityLogout from "./services/UserInactivity";
import useLogoutOnTabClose from "./hooks/useLogoutOnTabClose";
import { resetState as resetStateDueDate } from "./features/billingAndCollection/dueDateExtensionOverdueSlice";
import { resetState as resetStateInstallmentsNormal } from "./features/billingAndCollection/installmentsNormalSlice";
import { resetState as resetStateInstallmentsOverdue } from "./features/billingAndCollection/installmentsOverdueSlice";
import { resetOverallPerformanceState } from "./features/dashboard/overallPerformance";
import { resetState as resetStateColNormal } from "./features/generalServices/colNormalSlice";
import { resetState as resetStateColOverdue } from "./features/generalServices/colOverdueSlice";
import { resetState as resetStateConNormal } from "./features/generalServices/coaNormalSlice";
import { resetState as resetStateConOverdue } from "./features/generalServices/coaOverdueSlice";
import { resetState as resetStateCotNormal } from "./features/generalServices/cotNormalSlice";
import { resetState as resetStateCotOverdue } from "./features/generalServices/cotOverdueSlice";
import { resetState as resetStateNcNormal } from "./features/generalServices/ncNormalSlice";
import { fetchFeederStatus, resetFeederStatusState } from "./features/geoJson/feedersStatus";
import { signOut, setSelectedAdminCompanyCode, updateEmail, updateName, updatePhone, updateProfilePicture } from "./features/authentication/authenticationSlice";
import { updateOC } from "./features/officeCodeForName/officeCodeForName";
import {
  isDiscoAdminUser,
  readRestorableAdminCompanyCode,
} from "./utils/adminCompany";
import { allStatsAPI, removeStats } from "./features/stats/statsSlice";
import { fetchPaginatedAllComplaints, resetState as resetStateAllComplaints } from "./features/powerComplaints/getComplaintSlice";
import { fetchGeoJson, resetGeoJsonState } from "./features/geoJson/geoJson";
import { actionableOverdue, resetState as resetActionableOverdue } from "./features/actionable/actionableOverdueSlice";
import { resetState as resetStateNcOverdue } from "./features/generalServices/ncOverdueSlice";
import { actionableNormal, resetState as resetActionableNormal } from "./features/actionable/actionableNormalSlice";
import { cfpBaseUrl } from "./network/urls";
import { consumeTabCloseLogoutFlag } from "./utils/tabCloseLogout";

// Auth/layout stay in the first paint. Every screen is a separate chunk.
const CfcDashboard = lazy(() => import("./pages/Dashboard/CfcDashboard"));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const FieldOfficeDashboard = lazy(() => import("./pages/Dashboard/FieldOfficeDashboard"));
const NewConnectionForm = lazy(() => import("./pages/GeneralServices/NewConnection/NewConnectionForm"));
const Newconnectionupdatedocuments = lazy(() => import("./pages/GeneralServices/NewConnection/NewConnectionUpdateDocuments"));
const NewconnectionReports = lazy(() => import("./pages/Report/NewConnectionReports"));
const ChangeOfAttributesForm = lazy(() => import("./pages/GeneralServices/ChangeOfAttributes/ChangeOfAttributesForm"));
const Changeofloadupdatedocuments = lazy(() => import("./pages/GeneralServices/ChangeOfLoad/ChangeOfLoadUpdateDocuments"));
const ChangeofAttributesupdatedocuments = lazy(() => import("./pages/GeneralServices/ChangeOfAttribute/ChangeOfAttributesUpdateDocuments"));
const Changeoftariffupdatedocuments = lazy(() => import("./pages/GeneralServices/ChangeOfTariff/ChangeOfTariffUpdateDocuments"));
const CNICUpdation = lazy(() => import("./pages/GeneralServices/TenantCnicUpdation/TenantCnicUpdation"));
const OptForSmartMeterForm = lazy(() => import("./pages/GeneralServices/OptForSmartMeter/OptForSmartMeterForm"));
const Optforsmartmeterupdatedocuments = lazy(() => import("./pages/GeneralServices/OptForSmartMeter/OptForSmartMeterUpdateDocuments"));
const ShiftingToNetMeterForm = lazy(() => import("./pages/GeneralServices/ShiftingToNetMeter/ShiftingToNetMeterForm"));
const MeterChangeRequestForm = lazy(() => import("./pages/BillingAndCollection/MeterChangeRequest/MeterChangeRequestForm"));
const MeterChangeRequestUpdateDocuments = lazy(() => import("./pages/BillingAndCollection/MeterChangeRequest/MeterChangeRequestUpdateDocuments"));
const MeterReadingCorrectionForm = lazy(() => import("./pages/BillingAndCollection/MeterReadingCorrection/MeterReadingCorrectionForm"));
const MeterReadingCorrectionUpdateImages = lazy(() => import("./pages/BillingAndCollection/MeterReadingCorrection/MeterReadingCorrectionUpdateImages"));
const MeterReadingCorrectionReports = lazy(() => import("./pages/Report/MeterReadingCorrectionReports"));
const DueDateExtensionForm = lazy(() => import("./pages/BillingAndCollection/DueDateExtentsion/DueDateExtensionForm"));
const DueDateExtensionReport = lazy(() => import("./pages/Report/DueDateExtensionReports"));
const DuplicateBill = lazy(() => import("./pages/BillingAndCollection/DuplicateBill/DuplicateBill"));
const InstallmentsForm = lazy(() => import("./pages/BillingAndCollection/Installments/InstallmentsForm"));
const WithdrawalOfInstallmentForm = lazy(() => import("./pages/BillingAndCollection/WithdrawalOfInstallment/WithdrawalOfInstallmentForm"));
const InstallmentsReports = lazy(() => import("./pages/Report/InstallmentsReports"));
const PowerComplianTable = lazy(() => import("./pages/PowerComplaints/Complaints/PowerComplainTable"));
const Powercomplaintsform = lazy(() => import("./pages/PowerComplaints/Complaints/PowerComplaintsForm"));
const PowercomplaintsTrack = lazy(() => import("./pages/PowerComplaints/Complaints/PowerComplaintsTrack"));
const LoadManagement = lazy(() => import("./pages/PowerComplaints/LoadManagementSchedule/LoadManagement"));
const RoleCreationForm = lazy(() => import("./pages/AccessManagement/RoleManagement/create-role/RoleCreationForm"));
const RoleManagementTable = lazy(() => import("./pages/AccessManagement/RoleManagement/RoleManagementTable"));
const RoleUpdateForm = lazy(() => import("./pages/AccessManagement/RoleManagement/update-role/RoleUpdateForm"));
const UserCreateForm = lazy(() => import("./pages/AccessManagement/UserManagement/create-user/CreateUser"));
const UserUpdateForm = lazy(() => import("./pages/AccessManagement/UserManagement/update-user/UpdateUser"));
const UserManagementTable = lazy(() => import("./pages/AccessManagement/UserManagement/UserManagement"));
const CreatePermissson = lazy(() => import("./pages/AccessManagement/PermissionsManagement/create-permission/CreatePermissson"));
const PermissionManagementTable = lazy(() => import("./pages/AccessManagement/PermissionsManagement/PermissionManagementTable"));
const EopsUpdates = lazy(() => import("./pages/AccessManagement/EopsUpdates/EopsUpdates"));
const CompanyManagementTable = lazy(() => import("./pages/OrganizationManagement/CompanyManagement/CompanyManagement"));
const CompanyCreateForm = lazy(() => import("./pages/OrganizationManagement/CompanyManagement/CreateCompany/CreateCompany"));
const CompanyUpdateForm = lazy(() => import("./pages/OrganizationManagement/CompanyManagement/UpdateCompany/UpdateCompany"));
const CircleManagementTable = lazy(() => import("./pages/OrganizationManagement/CircleManagement/CircleManagement"));
const CircleCreateForm = lazy(() => import("./pages/OrganizationManagement/CircleManagement/CreateCircle/CreateCircle"));
const CircleUpdateForm = lazy(() => import("./pages/OrganizationManagement/CircleManagement/UpdateCircle/UpdateCircle"));
const DivisionCreateForm = lazy(() => import("./pages/OrganizationManagement/DivisionManagement/CreateDivision/CreateDivision"));
const DivisionManagementTable = lazy(() => import("./pages/OrganizationManagement/DivisionManagement/DivisionManagement"));
const DivisionUpdateForm = lazy(() => import("./pages/OrganizationManagement/DivisionManagement/UpdateDivision/UpdateDivision"));
const SupportCenterCreateForm = lazy(() => import("./pages/OrganizationManagement/SupportCenterManagement/create-support-center/CreateSupportCenter"));
const SupporCenterManagementTable = lazy(() => import("./pages/OrganizationManagement/SupportCenterManagement/SupportCenterManagementTable"));
const SupportCenterUpdateForm = lazy(() => import("./pages/OrganizationManagement/SupportCenterManagement/update-support-center/UpdateCenterManagement"));
const SubDivisionCreateForm = lazy(() => import("./pages/OrganizationManagement/SubDivisionManagement/CreateSubDivision/CreateSubDivision"));
const SubDivisionManagementTable = lazy(() => import("./pages/OrganizationManagement/SubDivisionManagement/SubDivisionManagement"));
const SubDivisionUpdateForm = lazy(() => import("./pages/OrganizationManagement/SubDivisionManagement/UpdateSubDivision/UpdateSubDivision"));
const SetAsideForm = lazy(() => import("./pages/BillingAndCollection/SetAside/SetAsideForm"));
const LoggedInUsers = lazy(() => import("./pages/common/loggedInUsers/loggedInUsers"));
const CommercialOperations = lazy(() => import("./pages/Dashboard/CommercialOperations"));
const HierarchicalDashboard = lazy(() => import("./pages/Dashboard/HierarchicalDashboard"));
const TechnicalOperations = lazy(() => import("./pages/Dashboard/TechinalOperations"));
const APMSDashboard = lazy(() => import("./pages/Dashboard/APMSDashboard"));
const LandingPage = lazy(() => import("./pages/Dashboard/LandingPage"));
const BillCorrectionForm = lazy(() => import("./pages/GeneralServices/Bill Correction/BillCorrectionForm"));
const ReconnectionForm = lazy(() => import("./pages/GeneralServices/Reconnection/ReconnectionForm"));
const ReconnectionUpdate = lazy(() => import("./pages/GeneralServices/Reconnection/ReconnectionUpdateDocuments"));
const DisconnectionForm = lazy(() => import("./pages/GeneralServices/Disconnection/DisconnectionForm"));
const DisconnectionUpdate = lazy(() => import("./pages/GeneralServices/Disconnection/DisconnectionUpdateDocuments"));
const ComplaintsSummary = lazy(() => import("./pages/PowerComplaints/Complaints/ComplaintsSummary"));
const DetailReport = lazy(() => import("./pages/PowerComplaints/Complaints/DetailReport"));
const DisplayComplaintGIS = lazy(() => import("./pages/PowerComplaints/Complaints/DisplayComplaintGIS"));
const APMSEscalatedComplaints = lazy(() => import("./pages/PowerComplaints/Complaints/APMSEscalatedComplaints"));
const APMSComplaintsSummary = lazy(() => import("./pages/PowerComplaints/Complaints/APMSComplaintsSummary"));
const SubdivisionWiseAPMSComplaintSummary = lazy(() => import("./pages/PowerComplaints/Complaints/SubdivisionWiseAPMSComplaintSummary"));
const CADLineComplaintsStats = lazy(() => import("./pages/PowerComplaints/Complaints/CADLineComplaintsStats"));
const FeederLengthReport = lazy(() => import("./pages/PowerComplaints/Complaints/FeederLengthReport"));
const UserComplaintProcessingStatistics = lazy(() => import("./pages/PowerComplaints/Complaints/UserComplaintProcessingStatistics"));
const LinemanAPPUserSummary = lazy(() => import("./pages/PowerComplaints/Complaints/LinemanAPPUserSummary"));
const LinemanStaffPerformance = lazy(() => import("./pages/PowerComplaints/Complaints/LineStaffPerformance"));
const PendingEscalatedTable = lazy(() => import("./pages/PowerComplaints/Complaints/PendingEscalatedComplaints"));
const BatchWiseAssessmentReport = lazy(() => import("./pages/Report/BatchWiseAssessmentReport"));
const CashCollectionSummaryReport = lazy(() => import("./pages/Report/CashCollectionSummaryReport"));
const DailyProgressReport = lazy(() => import("./pages/Report/DailyProgressReport"));
const DeadDefaulterList = lazy(() => import("./pages/Report/DeadDefaulterList"));
const FeederLossesReport = lazy(() => import("./pages/Report/FeederLossesReport"));
const NewConnectionExceededApplicationSummary = lazy(() => import("./pages/Report/NewConnectionExceededApplicationSummary"));
const NewConnectionPostedToIBSReport = lazy(() => import("./pages/Report/NewConnectionPostedToIBSReport"));
const TariffWiseAnalysisOfBillingAndCollectionReport = lazy(() => import("./pages/Report/TariffWiseAnalysisOfBillingAndCollectionReport"));
const EopsMobileAppUsageSummary = lazy(() => import("./pages/PowerComplaints/Complaints/EopsMobileAppUsageSummary"));
const NotLoggedInUserInLast24Hours = lazy(() => import("./pages/PowerComplaints/Complaints/NotLoggedInUserInLast24Hours"));
const NewConnectionStatusReport = lazy(() => import("./pages/Report/NewConnectionStatusReport"));
const MeterChangeOrderStatusReport = lazy(() => import("./pages/Report/MeterChangeOrderStatusReport"));
const EopsUsersStatus = lazy(() => import("./pages/PowerComplaints/Complaints/EopsUsersStatus"));
const MobileAppStats = lazy(() => import("./pages/common/MobileAppStats/MobileAppStats"));
const SRonEmail = lazy(() => import("./pages/Report/SRonEmail"));
const COASRonEmail = lazy(() => import("./pages/Report/COASRonEmail"));
const AmendDuplicateBill = lazy(() => import("./pages/BillingAndCollection/DuplicateBill/AmendDuplicateBill"));
const ComplaintSourceWise = lazy(() => import("./pages/PowerComplaints/Complaints/ComplaintSourceWise"));
const ComplaintDetailedHistory = lazy(() => import("./pages/PowerComplaints/Complaints/ComplaintDetailedHistory"));
const AdjustLastBillForm = lazy(() => import("./pages/BillingAndCollection/AdjustLastBill/AdjustLastBillForm"));
const AdjustLastBillUpdateDocuments = lazy(() => import("./pages/BillingAndCollection/AdjustLastBill/AdjustLastBillUpdateDocuments"));
const TopBestAndWorstPerformer = lazy(() => import("./pages/PowerComplaints/Complaints/TopBestAndWorstPerformer"));
const AmendBill2636Form = lazy(() => import("./pages/BillingAndCollection/AmendBill2636/AmendBill2636Form"));
const PartPaymentUpdateDocuments = lazy(() => import("./pages/BillingAndCollection/AmendBill2636/PartPaymentUpdateDocuments"));
const AdvancePaymentForm = lazy(() => import("./pages/BillingAndCollection/AdvancePayment/AdvancePaymentForm"));
const AdvancePaymentUpdateDocuments = lazy(() => import("./pages/BillingAndCollection/AdvancePayment/AdvancePaymentUpdateDocuments"));
const AdvancePaymentBillView = lazy(() => import("./pages/BillingAndCollection/AdvancePayment/AdvancePaymentBillView"));
const MeterReadingCorrectionBillView = lazy(() => import("./pages/BillingAndCollection/MeterReadingCorrection/MeterReadingCorrectionBillView"));
const NewConnectionStatusWiseCountReport = lazy(() => import("./pages/Report/NewConnectionStatusWiseCountReport"));
const NewConnectionCancelApplicationReport = lazy(() => import("./pages/Report/NewConnectionCancelApplicationReport"));
const ExceededMonthWisePendingRipeReport = lazy(() => import("./pages/Report/ExceededMonthWisePendingRipeReport"));
const ChangeOfAttributesCancelApplicationReport = lazy(() => import("./pages/Report/ChangeOfAttributesCancelApplicationReport"));
const ChangeOfAttributesStatusReport = lazy(() => import("./pages/Report/ChangeOfAttributesStatusReport"));
const ChangeOfAttributesPostedToIBSReport = lazy(() => import("./pages/Report/ChangeOfAttributesPostedToIBSReport"));
const ChangeOfAttributeStatusWiseCountReport = lazy(() => import("./pages/Report/ChangeOfAttributeStatusWiseCountReport"));
const COASummaryReports = lazy(() => import("pages/Report/COASummaryReports"));
const ReconnectionPostedToIBSReport = lazy(() => import("pages/Report/ReconnectionPostedToIBSReport"));
const ReconnectionStatusReport = lazy(() => import("./pages/Report/ReconnectionStatusReport"));
const PerformanceEvaluationReport = lazy(() => import("pages/Report/PerformanceEvaluationReport"));
const CancelApplicationsDueToNonPaymentReport = lazy(() => import("pages/Report/CancelApplicationsDueToNonPaymentReport"));
const MdmSyncReport = lazy(() => import("pages/Report/MdmSyncReport"));
const NewConnectionTable = lazy(() => import("./pages/GeneralServices/NewConnection/NewConnectionTable"));
const ChangeOfLoadTable = lazy(() => import("./pages/GeneralServices/ChangeOfLoad/ChangeOfLoadTable"));
const ChangeAttributesTable = lazy(() => import("./pages/GeneralServices/ChangeOfAttribute/ChangeAttributesTable"));
const ChangeOfTariffTable = lazy(() => import("./pages/GeneralServices/ChangeOfTariff/ChangeOfTariffTable"));
const OptForSmartMeterTable = lazy(() => import("./pages/GeneralServices/OptForSmartMeter/OptForSmartMeterTable"));
const ShiftingToNetMeterTable = lazy(() => import("./pages/GeneralServices/ShiftingToNetMeter/ShiftingToNetMeterTable"));
const MeterChangeRequestTable = lazy(() => import("./pages/BillingAndCollection/MeterChangeRequest/MeterChangeRequestTable"));
const MeterReadingCorrectionTable = lazy(() => import("./pages/BillingAndCollection/MeterReadingCorrection/MeterReadingCorrectionTable"));
const DueDateExtensionTable = lazy(() => import("./pages/BillingAndCollection/DueDateExtentsion/DueDateExtensionTable"));
const InstallmentsTable = lazy(() => import("./pages/BillingAndCollection/Installments/InstallmentsTable"));
const WithdrawalOfInstallmentTable = lazy(() => import("./pages/BillingAndCollection/WithdrawalOfInstallment/WithdrawalOfInstallmentTable"));
const ComplaintsGIS = lazy(() => import("./pages/PowerComplaints/Complaints/ComplaintsGIS"));
const SetAsideTable = lazy(() => import("./pages/BillingAndCollection/SetAside/SetAsideTable"));
const ActionablesTable = lazy(() => import("./pages/common/ActionablesTable/ActionablesTable"));
const BillCorrectionTable = lazy(() => import("./pages/GeneralServices/Bill Correction/BillCorrectionTable"));
const Reconnectiontable = lazy(() => import("./pages/GeneralServices/Reconnection/ReconnectionTable"));
const DisconnectionTable = lazy(() => import("./pages/GeneralServices/Disconnection/DisconnectionTable"));
const AllocatedComplaints = lazy(() => import("./pages/PowerComplaints/Complaints/AllocatedComplaints"));
const EscalatedComplaints = lazy(() => import("./pages/PowerComplaints/Complaints/EscalatedComplaints"));
const OnHoldCompliants = lazy(() => import("./pages/PowerComplaints/Complaints/OnHoldComplaints"));
const PendingCompliants = lazy(() => import("./pages/PowerComplaints/Complaints/PendingComplaints"));
const ResolvedComplaints = lazy(() => import("./pages/PowerComplaints/Complaints/ResolvedComplaints"));
const WorkingComplaints = lazy(() => import("./pages/PowerComplaints/Complaints/WorkingComplaints"));
const AdjustLastBillTable = lazy(() => import("./pages/BillingAndCollection/AdjustLastBill/AdjustLastBillTable"));
const AmendBill2636Table = lazy(() => import("./pages/BillingAndCollection/AmendBill2636/AmendBill2636Table"));
const AdvancePaymentTable = lazy(() => import("./pages/BillingAndCollection/AdvancePayment/AdvancePaymentTable"));

const App = () => {
  const dispatch = useDispatch();

  useInactivityLogout(); // Default is 30 mins
  useLogoutOnTabClose();

  useEffect(() => initCoaTableScrollShadow(), []);

  useEffect(() => {
    if (consumeTabCloseLogoutFlag()) {
      dispatch(signOut());
    }
  }, [dispatch]);

  const {
    userId,
    cscID,
    token,
    permissions,
    code,
    hierarchy,
    showAllApplicationsFlag,
    name,
    roleName,
    profilePicture,
    roleTypeId,
    phoneNo,
    roleId,
    cnic,
    email,
    circleCode,
    selectedAdminCompanyCode,
  } = useSelector((state) => state.auth);
  const persistRehydrated = useSelector((state) => state._persist?.rehydrated ?? false);

  useEffect(() => {
    if (!persistRehydrated || !userId || !token) {
      return;
    }
    if (!isDiscoAdminUser(code, roleId)) {
      return;
    }
    const storedCompanyCode = readRestorableAdminCompanyCode();
    if (!selectedAdminCompanyCode && storedCompanyCode) {
      dispatch(setSelectedAdminCompanyCode(storedCompanyCode));
      dispatch(updateOC({ officeCode: storedCompanyCode }));
    }
  }, [
    persistRehydrated,
    userId,
    token,
    code,
    roleId,
    selectedAdminCompanyCode,
    dispatch,
  ]);

  // useEffect(() => {
  //   const zoom = Math.round(window.devicePixelRatio * 100); 

  //   if (zoom < 100) {
  //     alert(`You're viewing at ${zoom}%. Press *Ctrl +* on Window or *Cmd +* on Mac to zoom in to 100%.`);
  //   } else if (zoom > 100) {
  //     alert(`You're viewing at ${zoom}%. Press *Ctrl -* on Window or *Cmd -* on Mac to zoom out to 100%.`);
  //   }
  // }, []);
  /**
    * @type {string|null}
    */
  /**
     * @param {string} token 
     */
  /**
   * Stores the selected NC item.
   * @type {Array}
   */
  const [ncSelectedItem, setNcSelectedItem] = useState();

  /**
  * Callback function to handle NC item selection.
  * @function
  * @param {Array} NCFilteredArray - The filtered NC items.
  */
  const NCCallBack = (NCFilteredArray) => {
    setNcSelectedItem(NCFilteredArray)
  }

    const callLogoutAPI = async () => {
      try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
  
  
        const requestOptions = {
          method: "PUT",
          headers: myHeaders,
          keepalive: true,
        };
  
        await fetch(cfpBaseUrl + "customerFaciliationPortal/logOut", requestOptions)
          .then((response) => response.json())
          .then((result) => {})
          .catch(() => {});
      } catch (e) {
  
      }
    }
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const storedDate = localStorage.getItem("appDate");
    if (!storedDate) {
      // localStorage.setItem("appDate", today);
      callLogoutAPI();
      dispatch(resetStateNcNormal());
      dispatch(resetStateNcOverdue());
      dispatch(resetStateConNormal());
      dispatch(resetStateConOverdue());
      dispatch(resetStateColNormal());
      dispatch(resetStateColOverdue());
      dispatch(resetStateCotNormal());
      dispatch(resetStateCotOverdue());
      dispatch(resetStateDueDate());
      dispatch(resetStateInstallmentsNormal());
      dispatch(resetStateInstallmentsOverdue());
      dispatch(removeStats());
      dispatch(resetOverallPerformanceState());
      dispatch(resetFeederStatusState());
      dispatch(resetGeoJsonState());
      dispatch(resetActionableNormal());
      dispatch(resetActionableOverdue());
      dispatch(resetStateAllComplaints());
      dispatch(signOut());
    } else if (storedDate !== today) {
    
      // logout + reset
      callLogoutAPI();
      dispatch(resetStateNcNormal());
      dispatch(resetStateNcOverdue());
      dispatch(resetStateConNormal());
      dispatch(resetStateConOverdue());
      dispatch(resetStateColNormal());
      dispatch(resetStateColOverdue());
      dispatch(resetStateCotNormal());
      dispatch(resetStateCotOverdue());
      dispatch(resetStateDueDate());
      dispatch(resetStateInstallmentsNormal());
      dispatch(resetStateInstallmentsOverdue());
      dispatch(removeStats());
      dispatch(resetOverallPerformanceState());
      dispatch(resetFeederStatusState());
      dispatch(resetGeoJsonState());
      dispatch(resetActionableNormal());
      dispatch(resetActionableOverdue());
      dispatch(resetStateAllComplaints());
      dispatch(signOut());
    } 
  }, []);


  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch("/version.json", { cache: "no-store" });
        const data = await response.json();
        const currentVersion = localStorage.getItem("appVersion");
        const versionPromptShown = localStorage.getItem("versionPromptShown");
    
        if (currentVersion && currentVersion !== data.version) {
  
          if (versionPromptShown !== data.version) {
            window.alert("A new version of this app is available.\nThe app will now update.");
  
            localStorage.setItem("appVersion", data.version);
            localStorage.removeItem("versionPromptShown");
            window.location.reload(true);
          }
        } else if (!currentVersion) {
          localStorage.setItem("appVersion", data.version);
        }
      } catch (error) {
      }
    };
  
    checkVersion();
  
    const interval = setInterval(checkVersion, 30000); 
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    const checkVersionLogout = async () => {
      try {
        const response = await fetch("/appLogoutVersion.json", { cache: "no-store" });
        const data = await response.json();
        const currentVersion = localStorage.getItem("appLogoutVersion");

        if (currentVersion && currentVersion !== data.version) {
          window.alert("A new version of this app is available.\nYou will be logged out.");
          dispatch(resetStateNcNormal());
          dispatch(resetStateNcOverdue());
          dispatch(resetStateConNormal());
          dispatch(resetStateConOverdue());
          dispatch(resetStateColNormal());
          dispatch(resetStateColOverdue());
          dispatch(resetStateCotNormal());
          dispatch(resetStateCotOverdue());
          dispatch(resetStateDueDate());
          dispatch(resetStateInstallmentsNormal());
          dispatch(resetStateInstallmentsOverdue());
          dispatch(removeStats());
          dispatch(resetOverallPerformanceState());
          dispatch(resetFeederStatusState());
          dispatch(resetGeoJsonState());
          dispatch(resetActionableNormal());
          dispatch(resetActionableOverdue());
          dispatch(resetStateAllComplaints());

          // Clear auth state (triggers purgeMiddleware to clear persisted storage)
          dispatch(signOut());

          // Clear all browser storage
          localStorage.clear();
          sessionStorage.clear();

          // Set new version after clearing
          localStorage.setItem("appLogoutVersion", data.version);

          // Redirect to login
          window.location.href = "/";
        } else if (!currentVersion) {
          localStorage.setItem("appLogoutVersion", data.version);
        }
      } catch (error) {
      }
    };

    checkVersionLogout();

    const interval = setInterval(checkVersionLogout, 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  return (
    <Grid sx={{ minHeight: "100%", backgroundColor: "transparent" }}>
      <BrowserRouter className="fullscreen-wrapper">
        <NetworkOfflineDialog />
        <Routes>
          <Route path="/">

            <Route index element={<Login />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="forgot-password" element={<Forgotpassword />} />
            <Route path="otp-verification" element={<OTPverification />} />
            <Route path="reset-password" element={<Resetpassword />} />


            <Route element={<ProtectedRoutes handleNCClick={NCCallBack} />}>


              <Route path="role-management">
                <Route
                  index
                  element={
                    <PermissionRoute requiredPermissions={["Access Management"]} allowRolePermissionAdmin>
                      <RoleManagementTable />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="new-role"
                  element={
                    <PermissionRoute requiredPermissions={["Access Management"]} allowRolePermissionAdmin>
                      <RoleCreationForm />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="update-role"
                  element={
                    <PermissionRoute requiredPermissions={["Access Management"]} allowRolePermissionAdmin>
                      <RoleUpdateForm />
                    </PermissionRoute>
                  }
                />
              </Route>

              <Route path="user-management">
                <Route
                  index
                  element={
                    <PermissionRoute requiredPermissions={["Access Management"]}>
                      <UserManagementTable />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="new-user"
                  element={
                    <PermissionRoute requiredPermissions={["Access Management"]}>
                      <UserCreateForm />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="update-user"
                  element={
                    <PermissionRoute requiredPermissions={["Access Management"]}>
                      <UserUpdateForm />
                    </PermissionRoute>
                  }
                />
              </Route>

              <Route path="permission-management">
                <Route
                  index
                  element={
                    <PermissionRoute requiredPermissions={["Access Management > Permission Management > Read"]} allowRolePermissionAdmin>
                      <PermissionManagementTable />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="new-permission"
                  element={
                    <PermissionRoute requiredPermissions={["Access Management > Permission Management > Create"]} allowRolePermissionAdmin>
                      <CreatePermissson />
                    </PermissionRoute>
                  }
                />

              </Route>

              <Route path="eops-updates" element={<EopsUpdates />} />

              <Route path="company-management">
                <Route
                  index
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <CompanyManagementTable />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="new-company"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <CompanyCreateForm />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="update-company"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <CompanyUpdateForm />
                    </PermissionRoute>
                  }
                />
              </Route>

              <Route path="circle-management">
                <Route
                  index
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <CircleManagementTable />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="new-circle"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <CircleCreateForm />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="update-circle"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <CircleUpdateForm />
                    </PermissionRoute>
                  }
                />
              </Route>

              <Route path="division-management">
                <Route
                  index
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <DivisionManagementTable />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="new-division"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <DivisionCreateForm />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="update-division"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <DivisionUpdateForm />
                    </PermissionRoute>
                  }
                />
              </Route>

              <Route path="support-center-management">
                <Route
                  index
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <SupporCenterManagementTable />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="new-support-center"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <SupportCenterCreateForm />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="UpdateCenterManagement"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <SupportCenterUpdateForm />
                    </PermissionRoute>
                  }
                />
              </Route>

              <Route path="sub-division-management">
                <Route
                  index
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <SubDivisionManagementTable />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="new-sub-division"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <SubDivisionCreateForm />
                    </PermissionRoute>
                  }
                />
                <Route
                  path="update-sub-division"
                  element={
                    <PermissionRoute requiredPermissions={["Organization Management"]}>
                      <SubDivisionUpdateForm />
                    </PermissionRoute>
                  }
                />
              </Route>

              {/* <Route path="company-management" >
                <Route index element={<CompanyManagementTable />} />
                <Route path='new-company' element={<CompanyCreateForm />}></Route>
                <Route path='update-company' element={<CompanyUpdateForm />}></Route>
              </Route>
              <Route path="circle-management" >
                <Route index element={<CircleManagementTable />} />
                <Route path='new-circle' element={<CircleCreateForm />}></Route>
                <Route path='update-circle' element={<CircleUpdateForm />}></Route>
              </Route>
              <Route path="division-management" >
                <Route index element={<DivisionManagementTable />} />
                <Route path='new-division' element={<DivisionCreateForm />}></Route>
                <Route path='update-division' element={<DivisionUpdateForm />}></Route>
              </Route>
              <Route path="support-center-management" >
                <Route index element={<SupporCenterManagementTable />} />
                <Route path='new-support-center' element={<SupportCenterCreateForm />}></Route>
                <Route path='UpdateCenterManagement' element={<SupportCenterUpdateForm />}></Route>
              </Route>
              <Route path="sub-division-management" >
                <Route index element={<SubDivisionManagementTable />} />
                <Route path='new-sub-division' element={<SubDivisionCreateForm />}></Route>
                <Route path='update-sub-division' element={<SubDivisionUpdateForm />}></Route>
              </Route> */}
              <Route path="landingpage" element={<LandingPage />} />
              <Route path="home" element={<Dashboard />} />
              <Route path="homecfc" element={<CfcDashboard />} />
              <Route path="homefieldoffice" element={<FieldOfficeDashboard />} />
              <Route path="operationalperformance" element={<HierarchicalDashboard />} />
              <Route path="commercialOperations" element={<CommercialOperations />} />
              <Route path="technicalOperations" element={<TechnicalOperations />} />
              <Route path="apms-dashboard" element={<APMSDashboard />} />

              <Route path="actionables-table" element={<ActionablesTable />} />
              <Route path="net-metering" >
                <Route index element={<ShiftingToNetMeterTable />} />
                <Route path='net-metering-form' element={<ShiftingToNetMeterForm />}></Route>
              </Route>
              <Route path="new-connection-table" element={<NewConnectionTable showTable={true} />} />
              <Route path="new-connection-posted-to-ibs-report" element={<NewConnectionPostedToIBSReport />} />
              <Route path="change-of-attributes-posted-to-ibs-report" element={<ChangeOfAttributesPostedToIBSReport />} />
              <Route path="new-connection-summary" element={<NewconnectionReports />} />
              <Route path="change-of-attributes-status-report" element={<ChangeOfAttributesStatusReport />} />
              <Route path="change-of-attributes-status-wise-count-report" element={<ChangeOfAttributeStatusWiseCountReport />} />
              <Route path="coa-cancel-application-report" element={<ChangeOfAttributesCancelApplicationReport />} />
              <Route path="sr-on-email" element={<SRonEmail />} />
              <Route path="coa-sr-on-email" element={<COASRonEmail />} />
              <Route path="change-of-attributes-summary-report" element={<COASummaryReports />} />
              <Route path="reconnection-posted-to-ibs-report" element={<ReconnectionPostedToIBSReport />} />
              <Route path="reconnection-status-report" element={<ReconnectionStatusReport />} />
              <Route path="performance-evaluation-report" element={<PerformanceEvaluationReport />} />
              <Route
                path="cancel-applications-due-to-non-payment-report"
                element={<CancelApplicationsDueToNonPaymentReport />}
              />
              <Route path="mdm-sync-report" element={<MdmSyncReport />} />
              <Route path="new-connection-status-wise-count-report" element={<NewConnectionStatusWiseCountReport />} />
              <Route path="nc-cancel-application-report" element={<NewConnectionCancelApplicationReport />} />
              <Route path="exceeded-month-wise-pending-ripe-report" element={<ExceededMonthWisePendingRipeReport />} />
              <Route path="new-connection-subdivision-wise-applications-status-report" element={<NewConnectionExceededApplicationSummary />} />
              <Route path="new-connection-form" element={<NewConnectionForm />} />
              <Route path="new-connection-update" element={<Newconnectionupdatedocuments />} />
              <Route path="reconnection-table" element={<Reconnectiontable showTable={true} />} />
              <Route path="disconnection-table" element={<DisconnectionTable showTable={true} />} />
              <Route path="bill-correction-form" element={<BillCorrectionForm />} />
              <Route path="logged-in-users" element={<LoggedInUsers />} />
              <Route path="bill-correction-table" element={<BillCorrectionTable showTable={true} />} />
              <Route path="reconnection-form" element={<ReconnectionForm />} />
              <Route path="reconnection-update" element={<ReconnectionUpdate />} />
              <Route path="disconnection-form" element={<DisconnectionForm />} />
              <Route path="disconnection-update" element={<DisconnectionUpdate />} />
              <Route path="change-of-attributes-table" element={<ChangeAttributesTable showTable={true} />} />
              <Route path="change-of-attributes-update" element={<ChangeofAttributesupdatedocuments />} /> 
              <Route path="change-of-tariff-table" element={<ChangeOfTariffTable showTable={true} />} />
              <Route path="change-of-tariff-update" element={<Changeoftariffupdatedocuments />} />
              <Route path="change-of-load-table" element={<ChangeOfLoadTable showTable={true} />} />
              <Route path="change-of-load-update" element={<Changeofloadupdatedocuments />} />
              <Route path="change-of-attributes-form/:sourcePage" element={<ChangeOfAttributesForm />} />
              <Route path="opt-for-smart-meter-table" element={<OptForSmartMeterTable />} />
              <Route path="opt-for-smart-meter-form" element={<OptForSmartMeterForm />} />
              <Route path="opt-for-smart-meter-update" element={<Optforsmartmeterupdatedocuments />} />
              <Route path="due-date-extension-form" element={<DueDateExtensionForm />} />
              <Route path="due-date-extension-table" element={<DueDateExtensionTable showTable={true} />} />

              <Route path="adjust-last-bill-form" element={<AdjustLastBillForm />} />
              <Route path="adjust-last-bill-table" element={<AdjustLastBillTable showTable={true} />} />
              <Route path="adjust-last-bill-update" element={<AdjustLastBillUpdateDocuments />} />

              <Route path="amend-2636-bill-form" element={<AmendBill2636Form />} />
              <Route path="amend-2636-bill-table" element={<AmendBill2636Table showTable={true} />} />
              <Route path="part-payment-update" element={<PartPaymentUpdateDocuments />} />

              <Route path="advance-payment-form" element={<AdvancePaymentForm />} />
              <Route path="advance-payment-table" element={<AdvancePaymentTable showTable={true} />} />
              <Route path="advance-payment-update" element={<AdvancePaymentUpdateDocuments />} />
              <Route path="advance-payment-bill" element={<AdvancePaymentBillView />} />
              {/* <Route path="amend-2636-bill-form" element={<AmendBill2636Form />} /> */}

              <Route path="due-date-extension-reports" element={<DueDateExtensionReport />} />
              <Route path="installments-form" element={<InstallmentsForm />} />
              <Route path="installments-table" element={<InstallmentsTable showTable={true} />} />
              <Route path="withdrawal-of-installment-form" element={<WithdrawalOfInstallmentForm />} />
              <Route path="withdrawal-of-installment-table" element={<WithdrawalOfInstallmentTable showTable={true} />} />
              <Route path="installments-reports" element={<InstallmentsReports />} />
              <Route path="new-connection-status-reports" element={<NewConnectionStatusReport />} />
              <Route path="meter-change-order-status-report" element={<MeterChangeOrderStatusReport />} />
              <Route path="set-aside-table" element={<SetAsideTable />} />
              <Route path="set-aside-form" element={<SetAsideForm />} />
              <Route path="meter-change-request-form" element={<MeterChangeRequestForm />} />
              <Route path="meter-change-request-table" element={<MeterChangeRequestTable showTable={true} />} />
              <Route path="meter-change-request-update" element={<MeterChangeRequestUpdateDocuments />} />
              <Route path="meter-reading-correction-form" element={<MeterReadingCorrectionForm />} />
              <Route path="meter-reading-correction-table" element={<MeterReadingCorrectionTable showTable={true} />} />
              <Route path="meter-reading-update" element={<MeterReadingCorrectionUpdateImages />} />
              <Route path="mrc-bill" element={<MeterReadingCorrectionBillView />} />
              <Route path="meter-reading-correction-reports" element={<MeterReadingCorrectionReports />} />
              <Route path="cnic-updation" element={<CNICUpdation />} />
              <Route path="power-complaint-table" element={<PowerComplianTable />} />
              <Route path="power-complaint-form" element={<Powercomplaintsform />} />
              <Route path="power-complaint-tracking" element={<PowercomplaintsTrack />} />
              <Route path="complaint-detailed-history" element={<ComplaintDetailedHistory />} />
              <Route path="load-shedding-schedule" element={<LoadManagement />} />
              <Route path="duplicate-bill" element={<DuplicateBill />} />
              <Route path="amend-duplicate-bill" element={<AmendDuplicateBill />} />

              <Route path="power-complaint-map" element={<ComplaintsGIS />} />
              <Route path="power-complaint-map-management" element={<ComplaintsGIS />} />
              <Route path="power-complaint-display-complaint" element={<DisplayComplaintGIS />} />
              <Route path="power-complaint-pending" element={<PendingCompliants />} />
              <Route path="power-complaint-allocated" element={<AllocatedComplaints />} />
              <Route path="power-complaint-working" element={<WorkingComplaints />} />
              <Route path="power-complaint-onhold" element={<OnHoldCompliants />} />
              <Route path="power-complaint-escalated" element={<EscalatedComplaints />} />
              <Route path="apms-escalated-complaints" element={<APMSEscalatedComplaints />} />
              <Route path="apms-complaints-summary" element={<APMSComplaintsSummary />} />
              <Route path="power-complaint-resolved" element={<ResolvedComplaints />} />
              <Route path="power-complaint-report" element={<ResolvedComplaints />} />
              <Route path="power-complaint-detail-report" element={<DetailReport />} />
              <Route path="eops-mobile-app-usage-summary" element={<EopsMobileAppUsageSummary />} />
              <Route path="top-best-and-worst-performer-users-report" element={<TopBestAndWorstPerformer />} />
              <Route path="eops-users-status-report" element={<EopsUsersStatus />} />
              <Route path="user-complaint-processing-statistics" element={<UserComplaintProcessingStatistics />} />
              <Route path="eops-complaint-source-wise" element={<ComplaintSourceWise />} />

              <Route path="not-logged-in-user-in-last-24-hours" element={<NotLoggedInUserInLast24Hours />} />
              <Route path="lineman-app-user-summary" element={<LinemanAPPUserSummary />} />
              {/* <Route path="mobile-app-stats" element={<MobileAppStats />} /> */}
              <Route path="mobile-app-stats" element={<MobileAppStats/>} />

              <Route path="lineman-staff-performance" element={<LinemanStaffPerformance />} />
              <Route path="subdivision-wise-complaints-summary" element={<ComplaintsSummary />} />
              <Route path="subdivision-wise-apms-complaint-summary" element={<SubdivisionWiseAPMSComplaintSummary />} />
              <Route path="cad-line-complaints-stats" element={<CADLineComplaintsStats />} />
              <Route path="feeder-length-report" element={<FeederLengthReport />} />
              <Route path="daily-progress-report" element={<DailyProgressReport />} />
              <Route path="daily-progress-report-cfc-wise" element={<DailyProgressReport variant="cfcWise" />} />
              <Route path="feeder-losses-report" element={<FeederLossesReport />} />
              <Route path="dead-defaulter-report" element={<DeadDefaulterList />} />
              <Route path="pending-escalated-complaints" element={<PendingEscalatedTable showTable={true} />} />
              <Route path="cash-collection-summary-report" element={<CashCollectionSummaryReport />} />
              <Route path="batch-wise-assessment-report" element={<BatchWiseAssessmentReport />} />
              <Route path="computed-billing-&-collection-analysis-report" element={<TariffWiseAnalysisOfBillingAndCollectionReport />} />
              <Route path="wrong-reading-table" element={<BatchWiseAssessmentReport />} />
              
            </Route>
          </Route>
          {/* Fallback Route */}
          <Route path="*" element={<Login />} />
        </Routes>
      </BrowserRouter>
    </Grid>
  );
};

export default App;